import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import {
  CircularProgress,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputProps,
  useMediaQuery,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'

import i18nUiComponents from '../../locales/i18n'
import { ClassesType } from '../../types'
import { handleFormTextInput } from '../../utils'
import { getBrowserName } from '../../utils/browserUtils'

const useStyles = makeStyles(
  theme => ({
    root: {
      paddingLeft: theme.spacing(2),
      backgroundColor: theme.colors.tableBackground,
      borderRadius: 20,
      height: 40,
      minHeight: 40,
      border: theme.constants.tabBorder,
    },
    activeRoot: {
      border: theme.constants.activeFieldBorder,
    },
    advancedRoot: {
      paddingLeft: 0,
    },
    input: {
      padding: 0,
      ...theme.utils.getPlaceholder(),
    },
    inputActive: {
      color: theme.colors.link,
    },
    searchContainer: {
      backgroundColor: theme.colors.searchIconBackground,
      borderRadius: '50%',
    },
    searchContainerActive: {
      backgroundColor: theme.colors.link,
    },
    iconButton: {
      padding: 9,
    },
    searchIcon: {
      color: theme.colors.secondaryText,
      width: 20,
      height: 20,
    },
    closeIcon: {
      color: theme.colors.tableBackground,
      width: 20,
      height: 20,
    },
    progressContainer: {
      display: 'flex',
    },
    progress: {
      color: theme.colors.disabledLabelText,
      marginRight: theme.spacing(1),
    },
    progressHidden: {
      visibility: 'hidden',
    },
    advancedSearchText: {
      fontSize: '1.4rem',
      color: theme.colors.tabLabel,
      cursor: 'pointer',
      userSelect: 'none',
    },
    advancedSearchIcon: {
      width: 20,
      height: 20,
    },
  }),
  { name: 'InlineSearch' },
)

export type InlineSearchHandle = {
  clearSearch?: () => void
}

export interface InlineSearchProps
  extends Omit<InputProps, 'onChange' | 'ref'> {
  readonly advancedSearch?: boolean
  readonly advancedSearchOpen?: boolean
  readonly classes?: ClassesType<typeof useStyles>
  readonly isLoading?: boolean
  readonly onAdvancedSearchOpen?: () => void
  readonly onChange: (value: string) => void
  readonly onClearSearch?: () => void
  readonly placeholder?: string
  readonly search?: string
  readonly showCloseButton?: boolean
}

const InlineSearch = forwardRef<InlineSearchHandle, InlineSearchProps>(
  function InlineSearch(
    {
      advancedSearch,
      advancedSearchOpen,
      search,
      placeholder,
      isLoading,
      onAdvancedSearchOpen = R.F,
      onChange,
      onFocus,
      onBlur,
      onClearSearch,
      classes: classesProp,
      showCloseButton = false,
      ...rest
    },
    ref,
  ) {
    const classes = useStyles({ classes: classesProp })

    const [activeState, setActiveState] = useState(false)

    const isSmallScreen = useMediaQuery('(max-width:1300px)')

    const buttonRef = useRef<HTMLButtonElement>(null)
    const buttonWrapperRef = useRef<HTMLDivElement>(null)

    const active = activeState || showCloseButton

    const clearSearch = () => {
      if (onClearSearch) {
        onClearSearch()
      } else {
        onChange('')
      }
    }

    useImperativeHandle(ref, () => ({
      clearSearch,
    }))

    const searchAdornment = (
      <InputAdornment position={advancedSearch ? 'start' : 'end'}>
        <Grid container alignItems="center" wrap="nowrap">
          {!advancedSearch && (
            <Grid item className={classes.progressContainer}>
              <CircularProgress
                className={classNames(classes.progress, {
                  [classes.progressHidden]: !isLoading,
                })}
                size={20}
              />
            </Grid>
          )}
          <Grid
            container
            alignItems="center"
            className={classNames(
              classes.searchContainer,
              active && classes.searchContainerActive,
            )}
            justifyContent="center"
            ref={buttonWrapperRef}
            tabIndex={0}
          >
            <IconButton
              disableRipple
              className={classes.iconButton}
              ref={buttonRef}
              size="large"
              onClick={() => {
                if (active) {
                  clearSearch()
                }
              }}
            >
              {active ? (
                <CloseIcon className={classes.closeIcon} />
              ) : (
                <SearchIcon className={classes.searchIcon} />
              )}
            </IconButton>
          </Grid>
        </Grid>
      </InputAdornment>
    )

    const advancedSearchAdornment = (
      <InputAdornment position="end">
        <Grid container item alignItems="center" wrap="nowrap">
          <CircularProgress
            className={classNames(classes.progress, {
              [classes.progressHidden]: !isLoading,
            })}
            size={20}
          />
          <label
            className={classes.advancedSearchText}
            htmlFor="advancedSearchBtn"
          >
            {isSmallScreen
              ? i18nUiComponents.t<string>('Abbreviations:COMMON.ADVANCED')
              : i18nUiComponents.t<string>('Search:ADVANCED_SEARCH')}
          </label>
          <IconButton
            disableRipple
            className={classes.iconButton}
            id="advancedSearchBtn"
            size="large"
            onClick={onAdvancedSearchOpen}
          >
            {advancedSearchOpen ? (
              <ExpandLess className={classes.advancedSearchIcon} />
            ) : (
              <ExpandMore className={classes.advancedSearchIcon} />
            )}
          </IconButton>
        </Grid>
      </InputAdornment>
    )

    const adornment = advancedSearch
      ? {
        startAdornment: searchAdornment,
        endAdornment: advancedSearchAdornment,
      }
      : {
        endAdornment: searchAdornment,
      }

    return (
      <Input
        disableUnderline
        fullWidth
        className={classNames(classes.root, {
          [classes.advancedRoot]: advancedSearch,
          [classes.activeRoot]: active,
        })}
        {...adornment}
        inputProps={{
          className: classNames(classes.input, active && classes.inputActive),
        }}
        placeholder={
          active
            ? ''
            : placeholder || i18nUiComponents.t<string>('Common:SEARCH_ACTION')
        }
        value={search}
        onBlur={event => {
          if (
            event?.relatedTarget === buttonRef?.current ||
            (getBrowserName() === 'Safari' &&
              event?.relatedTarget === buttonWrapperRef?.current)
          ) {
            clearSearch()
          }
          if (onBlur) {
            onBlur(event)
          }
          setActiveState(false)
        }}
        onChange={handleFormTextInput(onChange)}
        onFocus={event => {
          setActiveState(true)
          if (onFocus) {
            onFocus(event)
          }
        }}
        {...rest}
      />
    )
  },
)

export default InlineSearch
